import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { serverResponse } from "../../util/fakeServer";
import { language } from "../../languageProvider/language/language";
import { defaultLanguage } from "../../helper/utility";
import inpublishLogo from "../../assets/images/logo_inpublish.png";

/**
 * Fetcha un URL immagine e lo converte in data URL base64. Sidesteppa il
 * problema CORS di html2canvas: una volta che l'<img> ha src "data:image/...",
 * non c'è più richiesta cross-origin e la canvas non viene "tainted".
 * Se la fetch fallisce (CORS bloccato dal server), restituisce null così
 * il chiamante può decidere se mostrare un placeholder.
 */
async function fetchImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors", cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_e) {
    return null;
  }
}

// Stili inline per il layout PDF: il container vive nel DOM ma off-screen
// (left: -9999px) così html2canvas può catturarlo senza che l'utente lo veda.
const PDF_WIDTH_PX = 794; // A4 width @ 96dpi
const hiddenContainerStyle = {
  position: "fixed",
  left: "-9999px",
  top: 0,
  width: `${PDF_WIDTH_PX}px`,
  background: "#ffffff",
  color: "#111827",
  fontFamily: "Arial, Helvetica, sans-serif",
  padding: "32px",
  boxSizing: "border-box",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  borderBottom: "2px solid #E5E7EB",
  paddingBottom: "16px",
  marginBottom: "24px",
};

const logoStyle = {
  height: "48px",
  maxWidth: "120px",
  objectFit: "contain",
};

const productNameStyle = {
  fontSize: "16px",
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  margin: 0,
};

const titleStyle = {
  fontSize: "32px",
  fontWeight: 700,
  color: "#111827",
  margin: "0 0 24px",
};

const recipeImgStyle = {
  width: "100%",
  maxHeight: "320px",
  objectFit: "cover",
  borderRadius: "8px",
  marginBottom: "24px",
};

const sectionTitleStyle = {
  fontSize: "20px",
  fontWeight: 700,
  color: "#DC2626",
  margin: "24px 0 12px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const ingredientsListStyle = {
  fontSize: "14px",
  lineHeight: 1.7,
  color: "#374151",
  margin: 0,
  paddingLeft: "20px",
};

const ingredientItemStyle = {
  marginBottom: "6px",
};

const recipeHtmlStyle = {
  fontSize: "14px",
  lineHeight: 1.6,
  color: "#374151",
};

const footerStyle = {
  marginTop: "32px",
  paddingTop: "16px",
  borderTop: "1px solid #E5E7EB",
  fontSize: "11px",
  color: "#6B7280",
  textAlign: "center",
};

const buttonStyle = (disabled, compact) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: compact ? "0" : "6px",
  padding: compact ? "8px" : "6px 12px",
  fontSize: "13px",
  fontWeight: 600,
  color: disabled ? "#9CA3AF" : "var(--primary-color)",
  background: "transparent",
  border: `1px solid ${disabled ? "#9CA3AF" : "var(--primary-color)"}`,
  borderRadius: "6px",
  cursor: disabled ? "wait" : "pointer",
  transition: "background 0.15s, color 0.15s",
  whiteSpace: "nowrap",
  flexShrink: 0,
});

const DownloadIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const SpinnerIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ animation: "rotate 1s linear infinite" }}
  >
    <path d="M21 12a9 9 0 11-6.219-8.56" />
    <style>{`@keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </svg>
);

/**
 * Bottone per esportare la ricetta in PDF.
 * Visibile solo se il prodotto ha un marker hat-chef con `data` valido.
 *
 * @param {Object} markerInfo - il marker hat-chef del prodotto (con .title e .data)
 * @param {Object} productInfo - i dati del prodotto (per nome/brand nel header PDF)
 * @param {boolean} compact - se true, render icon-only (mobile)
 */
const RecipePdfButton = ({ markerInfo, productInfo, compact = false }) => {
  const [generating, setGenerating] = useState(false);
  const containerRef = useRef(null);

  // Guard: solo per hat-chef con data
  if (
    !markerInfo ||
    markerInfo.type !== "hat-chef" ||
    !markerInfo.data ||
    typeof markerInfo.data !== "object"
  ) {
    return null;
  }

  const { title = "", data = {} } = markerInfo;
  const recipeImg =
    Array.isArray(data.img) && data.img.length > 0 ? data.img[0] : null;
  const ingredients = data.ingredients || "";
  const recipeHtml = data.recipe || "";

  const productName =
    productInfo?.field1 || productInfo?.description || "Prodotto";
  // Logo header: cliente se presente, altrimenti fallback InPublish.
  // Il logo InPublish del footer è invece sempre presente.
  const clientIcon = serverResponse?.config?.clientIcon || null;
  const headerLogo = clientIcon || inpublishLogo;
  const leafletUrl =
    serverResponse?.config?.serverPath ||
    (typeof window !== "undefined" ? window.location.href : "");

  const handleClick = async () => {
    if (generating || !containerRef.current) return;
    setGenerating(true);
    try {
      const imgs = containerRef.current.querySelectorAll("img");

      // STEP 1: converti TUTTE le <img> esterne in data URL base64.
      // Senza questo passaggio, html2canvas non riesce a catturare le immagini
      // S3 perché il browser le considera cross-origin e la canvas viene
      // "tainted" → l'immagine sparisce dal PDF. Trasformandole in data URL
      // diventano locali al DOM e html2canvas le cattura senza problemi.
      await Promise.all(
        Array.from(imgs).map(async (img) => {
          const originalSrc = img.src;
          if (!originalSrc || originalSrc.startsWith("data:")) return;
          let dataUrl = await fetchImageAsDataUrl(originalSrc);
          // Se la fetch fallisce (CORS/404) e l'<img> ha un fallback
          // dichiarato via data-fallback-src, prova quello (tipicamente
          // un asset locale same-origin che fetcha sempre).
          if (!dataUrl && img.dataset.fallbackSrc) {
            dataUrl = await fetchImageAsDataUrl(img.dataset.fallbackSrc);
          }
          if (!dataUrl) return; // niente da fare, l'img resterà vuoto nel PDF
          img.src = dataUrl;
          // Aspetta che il browser ridipinga l'<img> con il nuovo src
          await new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.onload = resolve;
            img.onerror = resolve;
            setTimeout(resolve, 2000);
          });
        })
      );

      // STEP 2: cattura via html2canvas (ora le immagini sono data URL,
      // niente più problemi CORS).
      // windowWidth/Height forzano html2canvas a renderizzare come se il
      // viewport fosse desktop: senza questo, le media queries del CSS
      // globale (es. @media max-width) attive sul device reale alterano
      // la resa dei figli del container, producendo PDF diversi tra
      // desktop e mobile a parità di contenuto.
      const canvas = await html2canvas(containerRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: 1280,
        windowHeight: 1024,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Multi-pagina: se l'immagine è più alta di una pagina A4, aggiungiamo
      // pagine continuando a posizionare la stessa immagine traslata in alto.
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const safeTitle = (title || "ricetta").replace(/[^a-zA-Z0-9_]+/g, "_");
      pdf.save(`Ricetta_${safeTitle}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Errore generazione PDF ricetta:", err);
      // eslint-disable-next-line no-alert
      alert(
        "Si è verificato un errore durante la generazione del PDF. Riprova."
      );
    } finally {
      setGenerating(false);
    }
  };

  const buttonLabel =
    (language[defaultLanguage] && language[defaultLanguage].scarica_ricetta_pdf) ||
    "Scarica";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={generating}
        className="recipe-pdf-btn"
        style={buttonStyle(generating, compact)}
        aria-label={buttonLabel}
        title={compact ? buttonLabel : undefined}
      >
        {generating ? (
          <SpinnerIcon size={compact ? 18 : 16} />
        ) : (
          <DownloadIcon size={compact ? 18 : 16} />
        )}
        {!compact && (
          <span>{generating ? "Generazione…" : buttonLabel}</span>
        )}
      </button>

      {/* Layout PDF, off-screen */}
      <div ref={containerRef} style={hiddenContainerStyle} aria-hidden="true">
        {/* HEADER: logo azienda (fallback InPublish) + nome prodotto */}
        <div style={headerStyle}>
          <img
            src={headerLogo}
            alt=""
            style={logoStyle}
            crossOrigin="anonymous"
            data-fallback-src={inpublishLogo}
          />
          <div style={{ flex: 1 }}>
            <p style={productNameStyle}>{productName}</p>
          </div>
        </div>

        {/* TITOLO RICETTA + FOTO */}
        <h1 style={titleStyle}>{title}</h1>
        {recipeImg && (
          <img
            src={recipeImg}
            alt={title}
            style={recipeImgStyle}
            crossOrigin="anonymous"
          />
        )}

        {/* INGREDIENTI — split su ", " come fa ListModal nel modale */}
        {ingredients && (
          <>
            <h2 style={sectionTitleStyle}>Ingredienti</h2>
            <ul style={ingredientsListStyle}>
              {ingredients
                .split(", ")
                .filter((ing) => ing.trim().length > 0)
                .map((ing, i) => (
                  <li key={i} style={ingredientItemStyle}>
                    {ing.trim()}
                  </li>
                ))}
            </ul>
          </>
        )}

        {/* PREPARAZIONE */}
        {recipeHtml && (
          <>
            <h2 style={sectionTitleStyle}>Preparazione</h2>
            <div
              style={recipeHtmlStyle}
              dangerouslySetInnerHTML={{ __html: recipeHtml }}
            />
          </>
        )}

        {/* FOOTER: link al volantino + logo InPublish (sempre presente) */}
        <div style={footerStyle}>
          <p style={{ margin: "0 0 8px" }}>
            Scopri tutto il volantino su:{" "}
            <span style={{ color: "#DC2626", wordBreak: "break-all" }}>
              {leafletUrl}
            </span>
          </p>
          <img
            src={inpublishLogo}
            alt="InPublish"
            style={{
              height: "24px",
              maxWidth: "120px",
              objectFit: "contain",
              display: "inline-block",
              margin: "4px 0",
            }}
            crossOrigin="anonymous"
          />
          <p style={{ margin: 0, fontSize: "10px", color: "#9CA3AF" }}>
            Powered by inpublish
          </p>
        </div>
      </div>
    </>
  );
};

export default RecipePdfButton;
